import {useEffect, useRef, useState} from 'react';

import {view} from '@sail/ui';
import {animate, view as animatedView, AnimatePresence} from '@sail/engine';

import {Input} from '../Chat/Input';
import type {
  InputComponent as InputComponentType,
  InputProps,
} from '../Chat/InputProps';

const PLACEHOLDER_TRANSITION_DURATION_MS = 300;

export const InputWithTransitioningPlaceholder: React.FC<
  Omit<InputProps, 'placeholder'> & {
    initialPlaceholder: string;
    finalPlaceholder: string;
    triggered: boolean;
    InputComponent?: InputComponentType;
  }
> = ({
  initialPlaceholder,
  finalPlaceholder,
  triggered,
  InputComponent = Input,
  ...props
}) => {
  const [currentPlaceholder, setCurrentPlaceholder] = useState(
    triggered ? finalPlaceholder : initialPlaceholder,
  );
  const [isVisible, setIsVisible] = useState(true);
  const [isInputEmpty, setIsInputEmpty] = useState(true);
  // Only true during the brief transition window — AnimatePresence is mounted only then
  const [isAnimating, setIsAnimating] = useState(false);
  const hasTransitioned = useRef(triggered);

  useEffect(() => {
    if (!triggered || hasTransitioned.current) return;
    hasTransitioned.current = true;

    setIsAnimating(true);

    // Defer setIsVisible(false) to a separate render from setIsAnimating(true).
    // React 18 batches synchronous state updates — without this, AnimatePresence
    // mounts with no child and the exit animation never plays.
    let exitTimeout: ReturnType<typeof setTimeout>;
    let enterTimeout: ReturnType<typeof setTimeout>;
    let doneTimeout: ReturnType<typeof setTimeout>;
    const startTimeout = setTimeout(() => {
      setIsVisible(false);
      exitTimeout = setTimeout(() => {
        setCurrentPlaceholder(finalPlaceholder);
        enterTimeout = setTimeout(() => {
          setIsVisible(true);
          doneTimeout = setTimeout(
            () => setIsAnimating(false),
            PLACEHOLDER_TRANSITION_DURATION_MS,
          );
        }, 50);
      }, PLACEHOLDER_TRANSITION_DURATION_MS);
    }, 0);

    return () => {
      clearTimeout(startTimeout);
      clearTimeout(exitTimeout);
      clearTimeout(enterTimeout);
      clearTimeout(doneTimeout);
    };
  }, [triggered, finalPlaceholder]);

  const hasStagedFiles = props.stagedFiles && props.stagedFiles.length > 0;

  const handleSendMessage = async (message: string) => {
    setIsInputEmpty(true);
    return props.onSendMessage(message);
  };

  const placeholderCss = {
    zIndex: 1,
    left: '2.25rem',
    top: '0.7rem',
    position: 'absolute',
    lineHeight: '1.5rem',
    pointerEvents: 'none',
    touchAction: 'none',
    paddingRight: '3rem',
    textColor: 'form.placeholder',
  } as const;

  const renderOverlay = () => {
    if (!isInputEmpty || hasStagedFiles) return null;

    if (isAnimating) {
      return (
        <AnimatePresence>
          {isVisible ? (
            <animatedView.span
              key={currentPlaceholder}
              uses={[
                animate.in({
                  duration: PLACEHOLDER_TRANSITION_DURATION_MS,
                  opacity: 0,
                  transform: 'translateY(5px)',
                  easing: 'ease-out',
                }),
                animate.out({
                  duration: PLACEHOLDER_TRANSITION_DURATION_MS,
                  opacity: 0,
                  transform: 'translateY(-10px)',
                  easing: 'ease-in',
                }),
              ]}
              css={placeholderCss}
            >
              {currentPlaceholder}
            </animatedView.span>
          ) : null}
        </AnimatePresence>
      );
    }

    return <view.span css={placeholderCss}>{currentPlaceholder}</view.span>;
  };

  return (
    <view.div css={{maxHeight: 'fit-content'}}>
      <view.div css={{position: 'relative'}}>{renderOverlay()}</view.div>
      <InputComponent
        {...props}
        onSendMessage={handleSendMessage}
        aria-label={currentPlaceholder}
        onInputChanged={(v) => setIsInputEmpty(v.length === 0)}
      />
    </view.div>
  );
};
